import { Injectable } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { BASE_API_URL, IListing, IListingWhere, IPhoto, IPolicy, ISignalReception, IUser } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import {
  CachedHttpClient,
  Logger,
  OfflineListingDbSet,
  OfflineListingEntity,
  OfflineListingPhotoEntity,
  OfflineListingPhotosDbSet,
} from '@curbnturf/network';
import { PhotoService } from '@curbnturf/photo';
import { StatusFacade } from '@curbnturf/status/src/lib/status.facade';
import * as UserSelectors from '@curbnturf/user';
import { ToastController } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, takeWhile, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

const API_URL = BASE_API_URL + 'listing';

@Injectable({
  providedIn: 'root',
})
export class OfflineListingService {
  // For this service, start with the assumption that we are online.
  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'cellular' };

  get isOnline() {
    return this.connectionStatus.connected;
  }

  constructor(
    private http: CachedHttpClient,
    private logger: Logger,
    private offlineListingDbSet: OfflineListingDbSet,
    private offlineListingPhotosDbSet: OfflineListingPhotosDbSet,
    private photoService: PhotoService,
    private statusFacade: StatusFacade,
    private store: Store,
    private toastController: ToastController,
  ) {
    this.statusFacade.networkStatus$.subscribe((status) => {
      this.connectionStatus = status;
    });
  }

  create(listing: IListing): Observable<IListing> {
    const storableListing = { ...listing };
    if (!listing.syncId) {
      storableListing.syncId = uuidv4();
    }
    // @todo check for photos to update and store those for later upload
    return from(this.offlineListingDbSet.store(storableListing)).pipe(map(() => storableListing));
  }

  createPolicy(listing: IListing): Observable<IListing> {
    const policies: IPolicy[] = [];
    if (listing.policies) {
      policies.push(...listing.policies);
    }

    policies.push({ listing });

    return this.update({ ...listing, policies });
  }

  createSignalReception(listing: IListing): Observable<IListing> {
    const signalReception: ISignalReception[] = [];
    if (listing.signalReception) {
      signalReception.push(...listing.signalReception);
    }

    signalReception.push({ listing });

    return this.update({
      ...listing,
      signalReception,
    });
  }

  fetch(syncId: string): Observable<IListing> {
    return from(this.offlineListingDbSet.retrieve(syncId));
  }

  fetchAll(query: IListingWhere): Observable<IListing[]> {
    const queryString = objectToUrlParams(query);
    return this.http.get<IListing[]>(`${API_URL}?${queryString}`);
  }

  remove(syncId: string): Observable<IListing> {
    return from(this.offlineListingDbSet.retrieve(syncId)).pipe(
      mergeMap((listingToRemove: IListing) => {
        // remove photos
        if (listingToRemove.photos && listingToRemove.photos.length > 0) {
          return forkJoin(
            listingToRemove.photos?.map((photo) => {
              return from(this.offlineListingPhotosDbSet.removeByKey(photo.key)).pipe(
                map(() => {
                  return from(this.photoService.removeOfflinePhoto(photo.key)).pipe(
                    map(() => {
                      return listingToRemove;
                    }),
                  );
                }),
              );
            }),
          ).pipe(mergeMap(() => from(this.offlineListingDbSet.remove(syncId)).pipe(map(() => listingToRemove))));
        } else {
          return from(this.offlineListingDbSet.remove(syncId)).pipe(map(() => listingToRemove));
        }
      }),
    );
  }

  restore(listingId: number): Observable<IListing> {
    return this.http.post<IListing>(`${API_URL}/${listingId}/restore`, null);
  }

  removePolicy(listing: IListing, policy: IPolicy): Observable<IListing> {
    if (!listing.policies) {
      return of(listing);
    }
    const policies = [...listing.policies];

    const index = policies.findIndex((indexedPolicy) => indexedPolicy.id === policy.id);

    if (index >= 0) {
      policies.splice(index, 1);
    }

    return this.update({ ...listing, policies });
  }

  removeSignalReception(listing: IListing, signalReception: ISignalReception): Observable<IListing> {
    if (!listing.signalReception) {
      return of(listing);
    }

    const signalReceptions = [...listing.signalReception];

    const index = signalReceptions.indexOf(signalReception);

    if (index || index === 0) {
      signalReceptions.splice(index, 1);
    }

    return this.update({
      ...listing,
      signalReception: signalReceptions,
    });
  }

  update(listing: IListing): Observable<IListing> {
    // @todo check for photos to update and store those for later upload
    return from(this.offlineListingDbSet.store(listing)).pipe(map(() => listing));
  }

  enable(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/enable`, null);
  }

  enableAndHide(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/enableAndHide`, null);
  }

  disable(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/disable`, null);
  }

  // @todo rework for offline functionality
  removeImage(listingId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${listingId}/remove-image/${photoIndex}`);
  }

  uploadImage(syncId: string, photoFormData: FormData): Observable<{ syncId: string; photos: IPhoto[] }> {
    const file = photoFormData.get('files') as File;
    const caption = photoFormData.get('captions') as string;
    const entity: OfflineListingPhotoEntity = {
      syncId: syncId,
      key: 'unknown',
      caption,
    };

    return from(this.photoService.saveOfflinePhoto(file)).pipe(
      takeWhile((filename) => filename !== false),
      mergeMap((filename) => {
        if (filename) {
          entity.key = filename;
          const photo: IPhoto = {
            key: filename,
            caption: entity.caption,
          };
          // Check that we haven't already saved this photo to the database.
          return from(this.offlineListingPhotosDbSet.retrieveByKey(entity.key)).pipe(
            mergeMap((result) => {
              if (result.length === 0) {
                return from(this.offlineListingPhotosDbSet.store(entity)).pipe(map(() => ({ photos: [photo], syncId })));
              }
              return of({ photos: [photo], syncId });
            }),
          );
        } else {
          throw new Error('Failed to save uploaded image.');
        }
      }),
    );
  }

  // @todo rework for offline functionality
  favorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/favorite`, null);
  }

  // @todo rework for offline functionality
  unfavorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/unfavorite`, null);
  }

  uploadOfflineListings(): Observable<IListing[]> {
    return from(this.offlineListingDbSet.retrieveAll()).pipe(
      withLatestFrom(this.store.select(UserSelectors.getCurrentUser)),
      map(([listings, currentUser]: [IListing[] | OfflineListingEntity[], IUser]) => {
        return listings.map((listing) => {
          const photos: IPhoto[] = (listing as IListing)?.photos?.slice() || [];
          const listingToUpload: IListing = {
            ...listing,
            id: listing.id || undefined,
            photos: undefined,
            user: { id: currentUser.id },
          };
          for (const x in listingToUpload) {
            if (
              // @ts-ignore I really don't care in this context that it's "any"
              listingToUpload[x] === null ||
              // @ts-ignore I really don't care in this context that it's "any"
              listingToUpload[x] === '' ||
              // @ts-ignore I really don't care in this context that it's "any"
              (Array.isArray(listingToUpload[x]) && listingToUpload[x].length === 0)
            ) {
              // @ts-ignore I really don't care in this context that it's "any"
              listingToUpload[x] = undefined;
            }
          }

          return this.http.post<IListing>(API_URL, listingToUpload).pipe(
            exhaustMap((result: IListing) => {
              if (result.syncId && result.id) {
                const listingId: number = result.id;
                const syncId: string = result.syncId;
                if (Array.isArray(photos) && photos.length > 0) {
                  // Upload attached photos
                  return forkJoin(
                    photos.map((photo) => {
                      return this.uploadOfflinePhoto(photo, listingId, syncId).pipe(
                        map(() => {
                          return listing;
                        }),
                      );
                    }),
                  ).pipe(
                    mergeMap(() => {
                      return this.remove(syncId).pipe(
                        map((listing) => {
                          this.logger.debug('Offline listing removed.', { listingId, syncId: listing.syncId });
                          return listing;
                        }),
                      );
                    }),
                  );
                } else {
                  // No Photos exist on this record, remove the record.
                  return this.remove(syncId).pipe(
                    map((listing) => {
                      this.logger.debug('Offline listing removed.', { listingId, syncId: listing.syncId });
                      return listing;
                    }),
                  );
                }
              }

              return of(result);
            }),
            catchError(async (e) => {
              this.logger.error('Failed to Upload cached listing.' + (e.message || JSON.stringify(e)));
              const toast = await this.toastController.create({
                message: 'Failed to Upload Listing. Error:' + (e.message || JSON.stringify(e)),
                duration: 3000,
              });
              await toast.present();
            }),
          );
        });
      }),
      mergeMap((obsResult: Observable<IListing>[]) => forkJoin(obsResult)),
    );
  }

  uploadOfflinePhoto(photo: IPhoto, listingId: number, syncId: string): Observable<IPhoto | void> {
    this.logger.debug('Uploading Photo', { listingId, syncId, photo });
    return from(this.photoService.retrieveOfflinePhoto(photo.key)).pipe(
      mergeMap((file) => {
        if (file) {
          this.logger.debug('Photo found, proceeding to upload.', { listingId, syncId, photo });
          // Setup the file data to send to the server
          const formData = new FormData();
          formData.append('files', file);
          formData.append('captions', photo.caption || '');
          formData.append('listingId', listingId.toString());
          formData.append('syncId', syncId);

          return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${listingId}/upload-image`, formData).pipe(
            map((photoResult) => {
              this.logger.debug('Successfully uploaded image: ' + photo.key, {
                listingId,
                result: JSON.stringify(photoResult),
              });
              return photoResult.photos.pop();
            }),
            catchError((error) => {
              this.logger.error('Failed to upload listing image.', { listingId, photo, error });
              return of(error);
            }),
          );
        } else {
          this.logger.warning('Local photo not found.', { listingId, syncId, photo });
        }
        return of();
      }),
    );
  }
}
